import { t } from "i18next";
import { useTranslation } from "react-i18next";
import { object, string } from "yup";
import {
  Button,
  CenteredColBox,
  ErrorLabel,
  Form,
  Link,
} from "../../../common/components";
import { Box } from "../../../common/components/Box/Box";
import { useForm } from "../../../common/hooks/useForm";
import { spacing } from "../../../styles/theme";
import { UserOnboardingInfo } from "../../../types";
import { PROJECT_TYPES } from "../../../constant/common.constant";
import { OnboardingTitle } from "../Component/OnboardingTitle";
import { ProjectTypeListItem } from "./ProjectTypeListItem";
import { useEffect, MouseEvent, Dispatch, SetStateAction } from "react";
import Message, { MessageTypes } from "../../Message/Message";

interface Props {
  onPreviousStep: (e: MouseEvent<HTMLAnchorElement>) => void;
  setOnboardingInfo: Dispatch<SetStateAction<UserOnboardingInfo>>;
  onNextStep: (formValues: UserOnboardingInfo) => void;
  onboardingInfo: UserOnboardingInfo;
  isAuthorizedDistributor: boolean;
}

const validationSchema = object({
  forWho: string().required(t("general.errors.required.listItem")),
});

export const ProjectTypeList = ({
  onPreviousStep,
  setOnboardingInfo,
  onNextStep,
  onboardingInfo,
  isAuthorizedDistributor,
}: Props) => {
  const { t } = useTranslation();
  const formik = useForm({
    initialValues: onboardingInfo,
    onSubmit: (values) => {
      setOnboardingInfo((prev) => ({ ...prev, forWho: values.forWho }));
      onNextStep(values);
    },
    validationSchema: validationSchema,
  });

  useEffect(() => {
    if (isAuthorizedDistributor) {
      formik.setFieldValue("forWho", PROJECT_TYPES.DISTRIBUTOR);
    }
    // eslint-disable-next-line
  }, [isAuthorizedDistributor]);

  return (
    <Form formik={formik}>
      <Box fullWidth mt={{ xs: spacing.xlMobile, lg: 0 }}>
        <OnboardingTitle title={t("onboard.project_type.title")} />
        {isAuthorizedDistributor && (
          <Box mb={{ xs: spacing.regularMobile, lg: spacing.regularDesktop }}>
            <Message
              message={t("onboard.project_type.authorized_distributor_message")}
              type={MessageTypes.SUCCESS}
              messageIcon={"check_circle"}
            />
          </Box>
        )}
        <CenteredColBox
          gap={{ xs: spacing.smallMobile, lg: spacing.smallDesktop }}
          fullWidth
        >
          {Object.values(PROJECT_TYPES).map((type) => {
            return (
              <ProjectTypeListItem
                key={type}
                active={formik.values.forWho === type}
                type={type}
                onClick={() => formik.setFieldValue("forWho", type)}
              />
            );
          })}
          <ErrorLabel error={formik.errors.forWho} />
        </CenteredColBox>
        <CenteredColBox
          gap={{ xs: spacing.ctaSpacingMobile, lg: spacing.ctaSpacingDesktop }}
          mt={{ xs: spacing.regularMobile, lg: spacing.regularDesktop }}
          fullWidth
          data-test="onboard.projectType.submitButton"
        >
          <Button type="submit" fullWidth>
            {t("sign_in.continue")}
          </Button>
          <Link onClick={(e) => onPreviousStep(e)}>{t("onboard.back")}</Link>
        </CenteredColBox>
      </Box>
    </Form>
  );
};
